import React, { useContext } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { makeStyles } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import { formatNumber } from "../../services/utils/formatAndCompare";

import {
  blendColors,
  dangerColor,
  grayColor,
  whiteColor,
} from "../../assets/jss/material-dashboard-pro-react";
import AuthContext from "../../connections/auth/AuthProvider";

import { ThreeDots } from "react-loader-spinner";
import Tooltip from "@material-ui/core/Tooltip";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faX } from "@fortawesome/free-solid-svg-icons";

// @material-ui/icons
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";

// Import RouteFilter component
import RouteFilter from "./RouteFilter";

const styles = (clientColor) => ({
  plWrapper: {
    position: "relative",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    borderRadius: "5px",
    overflow: "hidden",
  },
  plHeader: {
    color: whiteColor,
    borderRadius: "5px 5px 0 0",
    paddingLeft: "5px",
    textAlign: "left",
    fontSize: "12px",
    paddingTop: "2px",
    paddingBottom: "2px",
    marginBottom: "0",
    marginTop: "0",
    margin: "0",
    backgroundColor: clientColor,
    flexShrink: 0,
    zIndex: 10,
  },
  plName: {
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    maxWidth: "80%",
    fontWeight: "bold",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    "&:hover": {
      opacity: 0.8,
    },
  },
  plActionWrapper: {
    fontSize: "14px",
    position: "absolute",
    right: "5px",
    top: "3px",
  },
  plAction: {
    display: "inline",
    margin: "0 5px",
    cursor: "pointer",
  },
  plOuterWrapper: {
    width: "100%",
    padding: "0",
    flex: 1,
    display: "block",
    fontSize: "12px",
    color: grayColor[6],
    whiteSpace: "nowrap",
    overflowY: "auto",
    overflowX: "auto",
    "&::-webkit-scrollbar": {
      width: "6px",
      height: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#c1c1c1",
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "#a1a1a1",
    },
  },
  plLineWrapper: {
    minHeight: "24px",
    display: "flex",
    lineHeight: "2",
    "& > div:first-child": {
      paddingLeft: "4px",
    },
    "& > div:last-child": {
      paddingRight: "4px",
    },
    transition: "opacity 0.2s",
    cursor: "pointer",
  },
  plLineDescription: {
    minWidth: "200px",
    width: "200px",
    textOverflow: "ellipsis",
    overflow: "hidden",
  },
  plColumnGroup: {
    display: "flex",
    flex: "0 0 auto",
    minWidth: "333px", // 3 columns * 110px + 3 borders (1px each) = 333px
  },
  plColumnGroupHeader: {
    flex: "1 1 auto",
    textAlign: "center",
    borderLeft: `1px solid ${grayColor[10]}`,
    minWidth: "333px", // 3 columns * 110px + 3 borders (1px each) = 333px
    backgroundColor: whiteColor,
    color: grayColor[1],
  },
  plValue: {
    flex: "0 0 110px",
    minWidth: "110px",
    maxWidth: "110px",
    textAlign: "right",
    paddingRight: "8px",
    borderLeft: `1px solid ${grayColor[12]}`,
  },
  plValueNegative: {
    color: dangerColor[0],
  },
  plNumber: {
    fontFamily: 'Menlo, Monaco, Consolas, "Courier New", monospace;',
  },
  plTableHeader: {
    color: whiteColor,
    backgroundColor: grayColor[6],
    position: "sticky",
    top: 0,
    zIndex: 10,
    "& > div:first-child": {
      backgroundColor: grayColor[6],
      color: whiteColor,
    },
  },
  plTopHeader: {
    position: "sticky",
    top: 0,
    zIndex: 11,
    "& > div:first-child": {
      backgroundColor: whiteColor,
    },
  },
  plSecondHeader: {
    position: "sticky",
    top: "24px", // Height of first header row
    zIndex: 10,
    marginTop: "0px",
  },
  plEloHeader: {
    backgroundColor: clientColor,
    color: whiteColor,
    fontSize: "12px",
    textTransform: "uppercase",
    cursor: "pointer",
    "& > div:first-child": {
      backgroundColor: clientColor,
      color: whiteColor,
      cursor: "pointer",
    },
  },
  plEloHeaderArrow: {
    float: "right",
    marginRight: "8px",
    fontSize: "10px",
  },
  plTotal: {
    backgroundColor: grayColor[8],
    textTransform: "uppercase",
    "& > div:first-child": {
      backgroundColor: grayColor[8],
    },
  },
  plSubTotal: {
    backgroundColor: grayColor[13],
    "& > div:first-child": {
      paddingLeft: "12px !important",
      backgroundColor: grayColor[13],
    },
  },
  plLevel3: {
    color: grayColor[1],
    "& > div:first-child": {
      backgroundColor: whiteColor,
    },
  },
  plDefaultLevel: {
    "& > div:first-child": {
      backgroundColor: whiteColor,
    },
  },
  plDrillDown: {
    "& > span:before": {
      fontFamily: "'Font Awesome 5 Free'",
      padding: "0 4px 0 0",
      fontWeight: "900",
      fontSize: "10px",
      cursor: "pointer",
    },
  },
  plDrillDownOpen: {
    "& > span:before": {
      content: "'\\f146'",
    },
  },
  plDrillDownClosed: {
    "& > span:before": {
      content: "'\\f0fe'",
    },
  },
  plDrillDownDisabled: {
    "& > span:before": {
      fontFamily: "'Font Awesome 5 Free'",
      padding: "0 4px 0 0",
      fontWeight: "900",
      fontSize: "10px",
      content: "'\\f0fe'",
      opacity: 0.3,
      cursor: "default",
    },
  },
  plIndentL2: {
    paddingLeft: "14px !important",
    "& > span:before": {
      color: grayColor[4],
      content: "'\\2517'",
      padding: "0 3px 0 0",
      fontSize: "10px",
    },
  },
  plIndentL2Parent: {
    paddingLeft: "14px !important",
  },
  plIndentL3: {
    paddingLeft: "28px !important",
    "& > span:before": {
      color: grayColor[4],
      content: "'\\2517'",
      padding: "0 3px 0 0",
      fontSize: "10px",
    },
  },
  highlighted: {
    backgroundColor:
      blendColors(grayColor[8], clientColor, 0.9) + " !important",
    "& > div:first-child": {
      backgroundColor:
        blendColors(grayColor[8], clientColor, 0.9) + " !important",
    },
  },
  faded: {
    opacity: "0.3",
  },
  hidden: {
    display: "none",
  },
  resizeHandle: {
    position: "absolute",
    right: "-3px", // Center the 6px handle on the border
    top: 0,
    bottom: 0,
    width: "6px",
    cursor: "col-resize",
    backgroundColor: "transparent",
    zIndex: 100,
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.1)",
    },
    "&:active": {
      backgroundColor: clientColor,
    },
  },
  resizing: {
    backgroundColor: clientColor + " !important",
    opacity: 0.5,
  },
});

const PriceRoutePnl = (props) => {
  let { userDetails } = useContext(AuthContext);
  const client_styles = () => ({
    ...styles(userDetails.implementation_color),
  });
  const useStyles = makeStyles(client_styles);
  const classes = useStyles();

  const { t } = useTranslation();

  const {
    plStructure,
    plName,
    drillDownsOpen,
    setDrillDownsOpen,
    removePnl,
    isLoading,
    hoveredRowIndex,
    setHoveredRowIndex,
    routeFilterOptions,
    routeFilters,
    onApplyRouteFilters,
    onClearRouteFilters,
    availableScenarios,
    currentScenario,
    onScenarioChange,
    // columnGroups is not used - we hardcode Indireto/Direto headers
    // timersAllowed is not used in Tax Route PnL
  } = props;

  // State for scenario dropdown menu
  const [anchorEl, setAnchorEl] = React.useState(null);
  const scenarioMenuOpen = Boolean(anchorEl);

  const handleScenarioMenuClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleScenarioMenuClose = () => {
    setAnchorEl(null);
  };

  const handleScenarioSelect = (scenario) => {
    if (onScenarioChange) {
      onScenarioChange(scenario);
    }
    handleScenarioMenuClose();
  };

  // State for column widths (name column + 4 data columns - removed separate % columns)
  const [columnWidths, setColumnWidths] = React.useState({
    name: 200,
    indireto_total: 110,
    indireto_unit: 120, // Default width for unit column with inline percentage
    direto_total: 110,
    direto_unit: 120, // Default width for unit column with inline percentage
  });

  // State for tracking resize
  const [resizing, setResizing] = React.useState(null);
  const [startX, setStartX] = React.useState(0);
  const [startWidth, setStartWidth] = React.useState(0);

  // Handle resize start
  const handleResizeStart = (columnKey, e) => {
    e.preventDefault();
    setResizing(columnKey);
    setStartX(e.clientX);
    setStartWidth(columnWidths[columnKey]);
  };

  // Handle resize move
  React.useEffect(() => {
    const handleMouseMove = (e) => {
      if (resizing) {
        const diff = e.clientX - startX;
        const newWidth = Math.max(80, startWidth + diff); // Minimum 80px
        setColumnWidths((prev) => ({
          ...prev,
          [resizing]: newWidth,
        }));
      }
    };

    const handleMouseUp = () => {
      setResizing(null);
    };

    if (resizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [resizing, startX, startWidth]);

  const handleDrillDownClick = (row_index) => {
    if (drillDownsOpen.includes(row_index)) {
      setDrillDownsOpen(drillDownsOpen.filter((item) => item !== row_index));
    } else {
      setDrillDownsOpen([...drillDownsOpen, row_index]);
    }
    return false;
  };

  // Helper function to check if an item should be visible based on its ancestor chain
  const shouldItemBeVisible = (item) => {
    if (item.is_header) {
      // ELO headers are always visible
      return true;
    }

    // First, check if this item's ELO section is collapsed
    // Find the header for this item by looking backwards for the nearest is_header row
    const itemIndex = plStructure.current.findIndex(
      (row) => row.row_index === item.row_index,
    );
    if (itemIndex !== -1) {
      // Look backwards to find the ELO header
      for (let i = itemIndex - 1; i >= 0; i--) {
        const row = plStructure.current[i];
        if (row.is_header) {
          // Found the ELO header - check if it's collapsed
          const headerRowId = row.row_index !== undefined ? row.row_index : i;
          if (!drillDownsOpen.includes(headerRowId)) {
            return false; // ELO section is collapsed
          }
          break; // Found the header, continue with normal parent checking
        }
      }
    }

    if (!item.parent) {
      // Top-level items (level 1 without parent) are visible if their ELO is expanded
      return true;
    }

    // Build parent lookup: for each row, find its immediate parent
    // Parent is identified by meta_name, but we need the CLOSEST one (by row_index)
    const findImmediateParent = (child) => {
      if (!child.parent) return null;

      // Find all rows with matching parent name
      const candidateParents = plStructure.current.filter(
        (row) => row.meta_name === child.parent,
      );

      if (candidateParents.length === 0) return null;
      if (candidateParents.length === 1) return candidateParents[0];

      // Multiple candidates - find the closest one by row_index (must be before this row)
      const validParents = candidateParents.filter(
        (p) => p.row_index < child.row_index,
      );

      if (validParents.length === 0) return null;

      // Return the closest parent (highest row_index that's still less than child's)
      return validParents.reduce((closest, current) =>
        current.row_index > closest.row_index ? current : closest,
      );
    };

    // Check if all ancestors in the chain are expanded
    let currentItem = item;
    const visitedRowIndices = new Set(); // Prevent infinite loops

    while (currentItem && currentItem.parent) {
      if (visitedRowIndices.has(currentItem.row_index)) break;
      visitedRowIndices.add(currentItem.row_index);

      // Find the immediate parent
      const parentItem = findImmediateParent(currentItem);

      if (!parentItem) break;

      // Check if parent is expanded using row_index
      if (!drillDownsOpen.includes(parentItem.row_index)) {
        return false;
      }

      currentItem = parentItem;
    }

    return true;
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) {
      return "-";
    }
    return formatNumber(value);
  };

  if (isLoading) {
    return (
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        <ThreeDots
          width={"120px"}
          color={userDetails.implementation_color}
          ariaLabel="loading"
        />
      </div>
    );
  }

  return (
    <div className={classes.plWrapper}>
      {/* region P&L Header */}
      <div className={classes.plHeader}>
        <div
          className={classes.plName}
          onClick={handleScenarioMenuClick}
          title={t("pnl.change_scenario")}
        >
          <span>{plName}</span>
          <ArrowDropDownIcon style={{ fontSize: "18px" }} />
        </div>
        <Menu
          anchorEl={anchorEl}
          open={scenarioMenuOpen}
          onClose={handleScenarioMenuClose}
          PaperProps={{
            style: {
              maxHeight: 300,
              width: "auto",
            },
          }}
        >
          {availableScenarios && availableScenarios.length > 0 ? (
            availableScenarios.map((scenario) => (
              <MenuItem
                key={scenario.scenario_id}
                onClick={() => handleScenarioSelect(scenario)}
                selected={
                  currentScenario &&
                  scenario.scenario_id === currentScenario.scenario_id
                }
                style={{
                  backgroundColor:
                    currentScenario &&
                    scenario.scenario_id === currentScenario.scenario_id
                      ? userDetails.implementation_color + "20"
                      : "transparent",
                }}
              >
                {scenario.scenario_name}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>
              <em>{t("pnl.no_scenarios_available")}</em>
            </MenuItem>
          )}
        </Menu>
        <div className={classes.plActionWrapper}>
          {removePnl && (
            <Tooltip title={t("pnl.remove")}>
              <div className={classes.plAction} onClick={removePnl}>
                <FontAwesomeIcon icon={faX} />
              </div>
            </Tooltip>
          )}
        </div>
      </div>
      {/* endregion */}

      {/* region Route Filter */}
      {routeFilterOptions && onApplyRouteFilters && onClearRouteFilters && (
        <RouteFilter
          clientColor={userDetails.implementation_color}
          filterOptions={routeFilterOptions}
          currentFilters={routeFilters || {}}
          onApplyFilters={onApplyRouteFilters}
          onClearFilters={onClearRouteFilters}
        />
      )}
      {/* endregion */}

      {/* region P&L Lines */}
      <div className={classes.plOuterWrapper}>
        {/* region Table Header with Column Groups */}
        <div
          className={cx(classes.plLineWrapper, classes.plTopHeader)}
          style={{
            backgroundColor: whiteColor,
            color: grayColor[1],
            minWidth: `${
              columnWidths.name +
              columnWidths.indireto_total +
              columnWidths.indireto_unit +
              columnWidths.direto_total +
              columnWidths.direto_unit +
              4 // borders (4 borders * 1px)
            }px`,
          }}
        >
          <div
            className={classes.plLineDescription}
            style={{
              backgroundColor: whiteColor,
              width: `${columnWidths.name}px`,
              minWidth: `${columnWidths.name}px`,
              maxWidth: `${columnWidths.name}px`,
              paddingBottom: "1px",
            }}
          ></div>
          {/* Indireto Group Header */}
          <div
            className={classes.plColumnGroup}
            style={{
              minWidth: `${columnWidths.indireto_total + columnWidths.indireto_unit + 2}px`,
              flex: "0 0 auto",
            }}
          >
            <div
              className={classes.plColumnGroupHeader}
              style={{
                minWidth: `${columnWidths.indireto_total + columnWidths.indireto_unit + 2}px`,
                width: `${columnWidths.indireto_total + columnWidths.indireto_unit + 2}px`,
                fontWeight: "bold",
              }}
            >
              Indireto
            </div>
          </div>
          {/* Direto Group Header */}
          <div
            className={classes.plColumnGroup}
            style={{
              minWidth: `${columnWidths.direto_total + columnWidths.direto_unit + 2}px`,
              flex: "0 0 auto",
            }}
          >
            <div
              className={classes.plColumnGroupHeader}
              style={{
                minWidth: `${columnWidths.direto_total + columnWidths.direto_unit + 2}px`,
                width: `${columnWidths.direto_total + columnWidths.direto_unit + 2}px`,
                fontWeight: "bold",
              }}
            >
              Direto
            </div>
          </div>
        </div>

        {/* region Sub-headers for each column */}
        <div
          className={cx(classes.plLineWrapper, classes.plSecondHeader)}
          style={{
            minWidth: `${
              columnWidths.name +
              columnWidths.indireto_total +
              columnWidths.indireto_unit +
              columnWidths.direto_total +
              columnWidths.direto_unit +
              4 // borders
            }px`,
            backgroundColor: grayColor[7],
          }}
        >
          <div
            className={classes.plLineDescription}
            style={{
              width: `${columnWidths.name}px`,
              minWidth: `${columnWidths.name}px`,
              maxWidth: `${columnWidths.name}px`,
              backgroundColor: grayColor[6],
              color: whiteColor,
            }}
          >
            <div
              style={{ position: "relative", width: "100%", height: "100%" }}
            >
              <div
                className={cx(classes.resizeHandle, {
                  [classes.resizing]: resizing === "name",
                })}
                onMouseDown={(e) => handleResizeStart("name", e)}
              />
            </div>
          </div>
          {/* Indireto Group */}
          <div
            className={classes.plColumnGroup}
            style={{
              minWidth: `${columnWidths.indireto_total + columnWidths.indireto_unit + 2}px`,
            }}
          >
            <div
              className={classes.plValue}
              style={{
                flex: `0 0 ${columnWidths.indireto_total}px`,
                minWidth: `${columnWidths.indireto_total}px`,
                maxWidth: `${columnWidths.indireto_total}px`,
                position: "relative",
                borderLeft: "none",
                textAlign: "center",
                backgroundColor: grayColor[1],
                color: whiteColor,
              }}
            >
              Total
              <div
                className={cx(classes.resizeHandle, {
                  [classes.resizing]: resizing === "indireto_total",
                })}
                onMouseDown={(e) => handleResizeStart("indireto_total", e)}
              />
            </div>
            <div
              className={classes.plValue}
              style={{
                flex: `0 0 ${columnWidths.indireto_unit}px`,
                minWidth: `${columnWidths.indireto_unit}px`,
                maxWidth: `${columnWidths.indireto_unit}px`,
                position: "relative",
                borderLeft: "none",
                textAlign: "center",
                backgroundColor: grayColor[1],
                color: whiteColor,
              }}
            >
              Unit
              <div
                className={cx(classes.resizeHandle, {
                  [classes.resizing]: resizing === "indireto_unit",
                })}
                onMouseDown={(e) => handleResizeStart("indireto_unit", e)}
              />
            </div>
          </div>
          {/* Direto Group */}
          <div
            className={classes.plColumnGroup}
            style={{
              minWidth: `${columnWidths.direto_total + columnWidths.direto_unit + 2}px`,
            }}
          >
            <div
              className={classes.plValue}
              style={{
                flex: `0 0 ${columnWidths.direto_total}px`,
                minWidth: `${columnWidths.direto_total}px`,
                maxWidth: `${columnWidths.direto_total}px`,
                position: "relative",
                borderLeft: "none",
                textAlign: "center",
                backgroundColor: grayColor[7],
                color: whiteColor,
              }}
            >
              Total
              <div
                className={cx(classes.resizeHandle, {
                  [classes.resizing]: resizing === "direto_total",
                })}
                onMouseDown={(e) => handleResizeStart("direto_total", e)}
              />
            </div>
            <div
              className={classes.plValue}
              style={{
                flex: `0 0 ${columnWidths.direto_unit}px`,
                minWidth: `${columnWidths.direto_unit}px`,
                maxWidth: `${columnWidths.direto_unit}px`,
                position: "relative",
                borderLeft: "none",
                textAlign: "center",
                backgroundColor: grayColor[7],
                color: whiteColor,
              }}
            >
              Unit
              <div
                className={cx(classes.resizeHandle, {
                  [classes.resizing]: resizing === "direto_unit",
                })}
                onMouseDown={(e) => handleResizeStart("direto_unit", e)}
              />
            </div>
          </div>
        </div>
        {/* endregion */}

        {/* region P&L Data Rows */}
        <div>
          {plStructure.current.map((val, key) => {
            const isVisible = shouldItemBeVisible(val);
            // Use row_index for unique identification across ELOs
            const uniqueRowId =
              val.row_index !== undefined ? val.row_index : key;

            // Check if this is the first ELO header
            const isFirstEloHeader = val.is_header && key === 0;

            // Check if this is the first visible row after an ELO header (for spacing)
            const isFirstRowAfterHeader =
              !val.is_header &&
              (() => {
                const currentIndex = plStructure.current.findIndex(
                  (row) => row.row_index === val.row_index,
                );
                if (currentIndex > 0) {
                  const previousRow = plStructure.current[currentIndex - 1];
                  return previousRow && previousRow.is_header;
                }
                return false;
              })();

            // Calculate lighter color for Indireto columns in ELO headers
            const lighterClientColor = val.is_header
              ? blendColors(whiteColor, userDetails.implementation_color, 0.3)
              : null;

            return (
              <div
                key={key}
                className={cx({
                  [classes.plLineWrapper]: true,
                  [classes.plDefaultLevel]: !val.is_header,
                  [classes.plEloHeader]: val.is_header,
                  [classes.plTotal]: !val.is_header && val.level === 1,
                  [classes.plSubTotal]: !val.is_header && val.level === 2,
                  [classes.plLevel3]: !val.is_header && val.level === 3,
                  [classes.highlighted]:
                    !val.is_header && hoveredRowIndex === uniqueRowId,
                  [classes.hidden]: !isVisible,
                })}
                style={{
                  minWidth: `${
                    columnWidths.name +
                    columnWidths.indireto_total +
                    columnWidths.indireto_unit +
                    columnWidths.direto_total +
                    columnWidths.direto_unit +
                    4 // borders
                  }px`,
                  marginTop: isFirstEloHeader
                    ? "0px"
                    : isFirstRowAfterHeader
                      ? "0px"
                      : undefined,
                  backgroundColor: val.is_header
                    ? userDetails.implementation_color
                    : undefined,
                }}
                onMouseEnter={() =>
                  !val.is_header &&
                  setHoveredRowIndex &&
                  setHoveredRowIndex(uniqueRowId)
                }
                onMouseLeave={() =>
                  !val.is_header &&
                  setHoveredRowIndex &&
                  setHoveredRowIndex(null)
                }
              >
                <div
                  className={cx({
                    [classes.plLineDescription]: true,
                    [classes.plDrillDown]: val.is_parent,
                    [classes.plDrillDownOpen]:
                      val.is_parent && drillDownsOpen.includes(uniqueRowId),
                    [classes.plDrillDownClosed]:
                      val.is_parent && !drillDownsOpen.includes(uniqueRowId),
                    [classes.plDrillDownDisabled]:
                      val.level === 1 && !val.is_parent,
                    [classes.plIndentL2Parent]:
                      val.level === 2 && val.is_parent,
                    [classes.plIndentL2]: val.level === 2 && !val.is_parent,
                    [classes.plIndentL3]: val.level === 3,
                  })}
                  onClick={(e) => {
                    // Handle drill-down click for headers and parent rows
                    // Skip if clicking on the resize handle
                    if (
                      (val.is_header || val.is_parent) &&
                      !e.target.classList.contains("resizeHandle")
                    ) {
                      handleDrillDownClick(uniqueRowId);
                    }
                  }}
                  style={{
                    cursor:
                      val.is_header || val.is_parent ? "pointer" : "default",
                    width: `${columnWidths.name}px`,
                    minWidth: `${columnWidths.name}px`,
                    maxWidth: `${columnWidths.name}px`,
                    position: "relative",
                    backgroundColor: val.is_header
                      ? userDetails.implementation_color
                      : undefined,
                    fontWeight:
                      val.is_header || val.level === 1 ? "bold" : "normal",
                  }}
                >
                  {/* Show arrow indicator for ELO headers on the right */}
                  {val.is_header && (
                    <span className={classes.plEloHeaderArrow}>
                      {drillDownsOpen.includes(uniqueRowId) ? (
                        <ChevronRightIcon
                          style={{ fontSize: "16px", verticalAlign: "middle" }}
                        />
                      ) : (
                        <ExpandMoreIcon
                          style={{ fontSize: "16px", verticalAlign: "middle" }}
                        />
                      )}
                    </span>
                  )}
                  {/* Show button/placeholder for all rows based on level */}
                  {!val.is_header && val.level === 1 && <span></span>}
                  {!val.is_header && val.level === 2 && val.is_parent && (
                    <span></span>
                  )}
                  {!val.is_header && val.level === 2 && !val.is_parent && (
                    <span></span>
                  )}
                  {!val.is_header && val.level === 3 && <span></span>}
                  {val.meta_name}
                  <div
                    className={cx(classes.resizeHandle, "resizeHandle", {
                      [classes.resizing]: resizing === "name",
                    })}
                    onMouseDown={(e) => handleResizeStart("name", e)}
                  />
                </div>

                {/* Indireto Group - 2 columns (Total, Unit with inline %) */}
                <div
                  className={classes.plColumnGroup}
                  style={{
                    minWidth: `${columnWidths.indireto_total + columnWidths.indireto_unit + 2}px`,
                  }}
                >
                  <div
                    className={cx(classes.plValue, classes.plNumber)}
                    style={{
                      flex: `0 0 ${columnWidths.indireto_total}px`,
                      minWidth: `${columnWidths.indireto_total}px`,
                      maxWidth: `${columnWidths.indireto_total}px`,
                      position: "relative",
                      borderLeft: val.is_header ? "none" : undefined,
                      backgroundColor: val.is_header
                        ? lighterClientColor
                        : undefined,
                    }}
                  >
                    {val.is_header ? "" : formatValue(val.indireto_total)}
                    <div
                      className={cx(classes.resizeHandle, {
                        [classes.resizing]: resizing === "indireto_total",
                      })}
                      onMouseDown={(e) =>
                        handleResizeStart("indireto_total", e)
                      }
                    />
                  </div>
                  <div
                    className={cx(classes.plValue, classes.plNumber)}
                    style={{
                      flex: `0 0 ${columnWidths.indireto_unit}px`,
                      minWidth: `${columnWidths.indireto_unit}px`,
                      maxWidth: `${columnWidths.indireto_unit}px`,
                      position: "relative",
                      borderLeft: val.is_header ? "none" : undefined,
                      backgroundColor: val.is_header
                        ? lighterClientColor
                        : undefined,
                    }}
                  >
                    {val.is_header ? (
                      ""
                    ) : (
                      <>
                        {val.indireto_pct !== null &&
                          val.indireto_pct !== undefined && (
                            <span
                              style={{
                                color: grayColor[2],
                                fontSize: "0.9em",
                                marginRight: "6px",
                              }}
                            >
                              ({val.indireto_pct.toFixed(1)}%)
                            </span>
                          )}
                        <span>{formatValue(val.indireto_unit)}</span>
                      </>
                    )}
                    <div
                      className={cx(classes.resizeHandle, {
                        [classes.resizing]: resizing === "indireto_unit",
                      })}
                      onMouseDown={(e) => handleResizeStart("indireto_unit", e)}
                    />
                  </div>
                </div>

                {/* Direto Group - 2 columns (Total, Unit with inline %) */}
                <div
                  className={classes.plColumnGroup}
                  style={{
                    minWidth: `${columnWidths.direto_total + columnWidths.direto_unit + 2}px`,
                  }}
                >
                  <div
                    className={cx(classes.plValue, classes.plNumber)}
                    style={{
                      flex: `0 0 ${columnWidths.direto_total}px`,
                      minWidth: `${columnWidths.direto_total}px`,
                      maxWidth: `${columnWidths.direto_total}px`,
                      position: "relative",
                      borderLeft: val.is_header ? "none" : undefined,
                      backgroundColor: val.is_header
                        ? userDetails.implementation_color
                        : undefined,
                    }}
                  >
                    {val.is_header ? "" : formatValue(val.direto_total)}
                    <div
                      className={cx(classes.resizeHandle, {
                        [classes.resizing]: resizing === "direto_total",
                      })}
                      onMouseDown={(e) => handleResizeStart("direto_total", e)}
                    />
                  </div>
                  <div
                    className={cx(classes.plValue, classes.plNumber)}
                    style={{
                      flex: `0 0 ${columnWidths.direto_unit}px`,
                      minWidth: `${columnWidths.direto_unit}px`,
                      maxWidth: `${columnWidths.direto_unit}px`,
                      position: "relative",
                      borderLeft: val.is_header ? "none" : undefined,
                      backgroundColor: val.is_header
                        ? userDetails.implementation_color
                        : undefined,
                    }}
                  >
                    {val.is_header ? (
                      ""
                    ) : (
                      <>
                        {val.direto_pct !== null &&
                          val.direto_pct !== undefined && (
                            <span
                              style={{
                                color: grayColor[2],
                                fontSize: "0.9em",
                                marginRight: "6px",
                              }}
                            >
                              ({val.direto_pct.toFixed(1)}%)
                            </span>
                          )}
                        <span>{formatValue(val.direto_unit)}</span>
                      </>
                    )}
                    <div
                      className={cx(classes.resizeHandle, {
                        [classes.resizing]: resizing === "direto_unit",
                      })}
                      onMouseDown={(e) => handleResizeStart("direto_unit", e)}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {/* endregion */}
      </div>
      {/* endregion */}
    </div>
  );
};

PriceRoutePnl.defaultProps = {
  drillDownsOpen: [],
  timersAllowed: true,
};

PriceRoutePnl.propTypes = {
  plStructure: PropTypes.object.isRequired,
  plName: PropTypes.string,
  drillDownsOpen: PropTypes.array.isRequired,
  setDrillDownsOpen: PropTypes.func.isRequired,
  removePnl: PropTypes.func,
  isLoading: PropTypes.bool.isRequired,
  columnGroups: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      columns: PropTypes.arrayOf(PropTypes.string).isRequired,
    }),
  ),
  timersAllowed: PropTypes.bool,
  mouseOverPLLine: PropTypes.string,
  mouseOverPLLineFade: PropTypes.string,
  mouseOverVal: PropTypes.bool,
  handleMouseLeavePLLine: PropTypes.func,
  handleMouseOverPLLine: PropTypes.func,
  handleMouseLeavePLVal: PropTypes.func,
  handleMouseOverPLVal: PropTypes.func,
  hoveredRowIndex: PropTypes.number,
  setHoveredRowIndex: PropTypes.func,
  availableScenarios: PropTypes.arrayOf(
    PropTypes.shape({
      scenario_id: PropTypes.number.isRequired,
      scenario_name: PropTypes.string.isRequired,
    }),
  ),
  currentScenario: PropTypes.shape({
    scenario_id: PropTypes.number.isRequired,
    scenario_name: PropTypes.string.isRequired,
  }),
  onScenarioChange: PropTypes.func,
  routeFilterOptions: PropTypes.shape({
    node1_reference_productionsite: PropTypes.array,
    node1_location: PropTypes.array,
    node2_reference_client: PropTypes.array,
    node2_location: PropTypes.array,
    node3_reference_pointofsale: PropTypes.array,
    node3_location: PropTypes.array,
  }),
  routeFilters: PropTypes.shape({
    node1_reference_productionsite: PropTypes.string,
    node1_location: PropTypes.string,
    node2_reference_client: PropTypes.string,
    node2_location: PropTypes.string,
    node3_reference_pointofsale: PropTypes.string,
    node3_location: PropTypes.string,
  }),
  onApplyRouteFilters: PropTypes.func,
  onClearRouteFilters: PropTypes.func,
};

export default PriceRoutePnl;
